/**
 * IEngineContext — the single, capability-scoped runtime boundary between
 * "programmable code" (scripts, behaviours, engine systems) and engine internals.
 *
 * It is the runtime sibling of the editor's command-oriented `EditorXxxAPI` facade
 * (they share stable types like {@link EntityHandle} / {@link ComponentToken} but are
 * deliberately different objects: the editor facade is undoable/command-oriented, this
 * one is allocation-free).
 *
 * This is the ONE merge point for the two previously-incompatible `EngineBridge`
 * contracts (`packages/redprint/src/runtime/ExecutionContext.ts` and
 * `packages/redprint-js/src/JSCompiler.ts`): {@link IEngineBridge} is the
 * scripting-facing view of `IEngineContext`, and {@link IEngineSystemContext} is the
 * wider view handed to engine-system modules.
 *
 * Design invariants:
 *  - Capability-composed, never a god-interface. Consumers receive only the
 *    capabilities their scope allows (a sandboxed user script gets a narrow context;
 *    an engine-system module gets a wide one incl. raw column access).
 *  - Entity-bound at construction: per-entity calls read `ctx.transform.setPosition(x, y)`,
 *    never `bridge.setPosition(entityId, x, y)`.
 *  - Allocation-free on the hot path: capability objects are built once per executor;
 *    per-entity hot data is reached through generated struct-of-getter Views
 *    (see {@link ComponentToken}), not per-field method calls.
 *  - Versioned: {@link IEngineContext.apiVersion} is asserted at load/attach time so
 *    generated TS `.d.ts` and C# `.g.cs` projections can refuse a mismatched host.
 */

import type { EngineApi } from "./EngineApi.generated";

// ============================================================================
// Primitives
// ============================================================================

/**
 * A runtime entity handle — a dense integer that indexes the SoA component
 * columns in lockstep. Equivalent to a `GameObjectId` at runtime; it is NOT
 * persisted (serialization uses the stable string `typeId` + GUID instead).
 */
export type EntityHandle = number;

/** Sentinel for "no entity" (system-level contexts are not entity-bound). */
export const NO_ENTITY: EntityHandle = -1;

/** A plain 2-component vector value (no engine `Vec2` dependency at the boundary). */
export interface Vec2Like {
  x: number;
  y: number;
}

/** Boundary API version. Asserted at load/attach time across language projections. */
export interface ApiVersion {
  readonly major: number;
  readonly minor: number;
}

/**
 * A branded, type-carrying handle to a component type — the codegen-as-reflection
 * primitive that replaces runtime reflection (`GetComponent<T>()`).
 *
 * Concrete tokens are emitted by the component-registry codegen into
 * `ComponentTable.generated.ts`, e.g.
 * `export const Transform: ComponentToken<TransformView> = ct(3, 'core.Transform')`.
 * The numeric `id` indexes a dense slot array (an array index, not a string hash),
 * and the phantom `TView` gives full return typing with no casts — identical
 * discipline for V8-TS now and NativeAOT-C# later (neither reflects at runtime).
 */
export interface ComponentToken<TView = unknown> {
  /** Dense, runtime-only numeric id (derived deterministically from `typeId`). */
  readonly id: number;
  /** Stable serialization id, e.g. `"core.Transform"`. */
  readonly typeId: string;
  /** Phantom — never present at runtime; carries the view/data type for `T`. */
  readonly __view?: TView;
}

/** Unsubscribe handle returned by event/subscription capabilities. */
export type Unsubscribe = () => void;

// ============================================================================
// Capabilities (composed into a context; never a single god-interface)
// ============================================================================

/** Frame timing. Read-only per-frame values, set by the host loop. */
export interface TimeCaps {
  /** Seconds since the runtime started. */
  readonly time: number;
  /** Seconds elapsed since the previous frame (variable step). */
  readonly deltaTime: number;
  /** Fixed timestep in seconds (physics step). */
  readonly fixedDeltaTime: number;
}

/** Diagnostics. */
export interface LogCaps {
  log(...args: unknown[]): void;
  warn(...args: unknown[]): void;
  error(...args: unknown[]): void;
}

/**
 * Input, addressed by *action name* (the canonical form — the redprint-js variant
 * won per W4). Raw key access is a separate, scoped extension ({@link RawKeyInputCaps}).
 */
export interface InputCaps {
  /** Analog/axis value for an action, typically in [-1, 1] or [0, 1]. */
  getInput(action: string): number;
  /** Held this frame. */
  isInputPressed(action: string): boolean;
  /** Went down this frame. */
  isInputDown(action: string): boolean;
  /** Went up this frame. */
  isInputUp(action: string): boolean;
}

/** Raw keyboard access — a scoped extension, only granted to trusted contexts. */
export interface RawKeyInputCaps {
  isKeyDown(key: string): boolean;
  isKeyPressed(key: string): boolean;
}

/** A read-only world-space snapshot of an entity's transform (for reading OTHER entities). */
export interface TransformSnapshot {
  readonly position: Vec2Like;
  /** Rotation in degrees (matches the runtime Transform convention). */
  readonly rotation: number;
  readonly scale: Vec2Like;
}

/** Transform of the bound entity. */
export interface TransformCaps {
  getPosition(): Vec2Like;
  setPosition(x: number, y: number): void;
  translate(dx: number, dy: number): void;
  getRotation(): number;
  setRotation(angleDeg: number): void;
  rotate(deltaDeg: number): void;
  getScale(): Vec2Like;
  setScale(x: number, y: number): void;
}

/** Sprite of the bound entity. */
export interface SpriteCaps {
  /** Packed `0xRRGGBB`. */
  setColor(rgb: number): void;
  /** [0, 1]. */
  setAlpha(alpha: number): void;
}

/** A single physics query result. */
export interface RaycastHit {
  readonly entity: EntityHandle;
  readonly point: Vec2Like;
  readonly normal: Vec2Like;
  readonly distance: number;
}

/**
 * Payload delivered to a Behaviour's `onCollisionEnter`/`onTriggerEnter`/… hooks. `other` is the
 * other entity's handle; contact/normal are present for collisions, absent for triggers.
 */
export interface CollisionInfo {
  readonly other: EntityHandle;
  readonly contactX?: number;
  readonly contactY?: number;
  readonly normalX?: number;
  readonly normalY?: number;
}

/**
 * An input-action phase transition, delivered to the entity holding the `input.InputReceiver`
 * via `triggerEvent('events.inputAction', …)` → `Behaviour.onInputAction`. The event-driven
 * alternative to polling `isInputDown` every frame: `started`/`performed` fire on activation
 * (press / interaction completion), `canceled` on release.
 */
export interface InputActionEvent {
  /** Action name as authored in the input asset (e.g. `'Jump'`). */
  readonly action: string;
  /** Action map the action belongs to (e.g. `'Gameplay'`). */
  readonly map: string;
  readonly phase: 'started' | 'performed' | 'canceled';
  /** Action value at the transition — button: boolean, value: number, axis2d/3d: vector. */
  readonly value: boolean | number | Vec2Like | { x: number; y: number; z: number };
  /** Control path that triggered it (e.g. `'<Mouse>/leftButton'`). */
  readonly control: string;
  /** Device id (e.g. `'keyboard/0'`). */
  readonly device: string;
  /** Seconds since the action entered `started`. */
  readonly duration: number;
}

/**
 * Physics queries. Collision *events* are not delivered here — they arrive via
 * {@link import('./IScriptExecutor').IScriptExecutor.triggerEvent}, symmetrically
 * to every backend.
 */
export interface PhysicsCaps {
  raycast(origin: Vec2Like, direction: Vec2Like, maxDistance?: number): RaycastHit | null;
  overlapCircle(center: Vec2Like, radius: number): readonly EntityHandle[];
}

/** Scoped pub/sub for gameplay events. */
export interface EventCaps {
  emit(eventId: string, payload?: Record<string, unknown>): void;
  on(eventId: string, handler: (payload?: Record<string, unknown>) => void): Unsubscribe;
}

/**
 * The entity/component world. Token-based component access is the canonical,
 * allocation-free, reflection-free path; the string-keyed escape hatch lives on
 * `GameObject.getComponentData(typeId)` for dynamic/editor code.
 */
export interface WorldCaps {
  spawn(prefab?: string): EntityHandle;
  destroy(entity: EntityHandle): void;
  addComponent<T>(entity: EntityHandle, token: ComponentToken<T>): T;
  removeComponent(entity: EntityHandle, token: ComponentToken<unknown>): boolean;
  getComponent<T>(entity: EntityHandle, token: ComponentToken<T>): T | null;
  hasComponent(entity: EntityHandle, token: ComponentToken<unknown>): boolean;
  /** Iterate entities that have all of the given component types. */
  query(...tokens: readonly ComponentToken<unknown>[]): Iterable<EntityHandle>;
  /**
   * Resolve a stable GameObject GUID to its runtime entity handle, or `null` if not present.
   * The script-side counterpart of `Level.findByGuid` — needed by logic that references other
   * objects by their persisted GUID (e.g. a camera following a target).
   */
  getEntityByGuid(guid: string): EntityHandle | null;
  /**
   * Read another entity's world-space transform snapshot (or `null` if it doesn't exist).
   * The bound entity uses {@link TransformCaps} (`this.transform`); reading OTHER entities (a
   * camera target, an AI's prey) goes through here.
   */
  getTransform(entity: EntityHandle): TransformSnapshot | null;
  /**
   * Write ANOTHER entity's local transform. The bound entity uses {@link TransformCaps};
   * driving other entities (a manager script animating a pool of particles/props) goes through
   * here. Flat args, no option objects — these sit on per-frame hot paths. No-ops on a missing
   * entity.
   */
  setPosition(entity: EntityHandle, x: number, y: number): void;
  setRotation(entity: EntityHandle, angleDeg: number): void;
  setScale(entity: EntityHandle, sx: number, sy: number): void;
}

// ============================================================================
// Contexts
// ============================================================================

/**
 * The capability-scoped runtime context handed to scripts, behaviours and the
 * Redprint interpreter. Built once per executor; `entity` is bound at creation.
 *
 * Concrete implementations exist per runtime (editor play-mode, browser player,
 * .NET player) and are injected (DI) instead of reaching module-global singletons
 * like `getTransformStorage()`.
 */
export interface IEngineContext {
  readonly apiVersion: ApiVersion;
  /** The entity this context is bound to, or {@link NO_ENTITY} for system contexts. */
  readonly entity: EntityHandle;

  /**
   * The global-services surface (`engine.physics`, `engine.events`, …) — assembled from the
   * registered {@link import('./ScriptApiRegistry').ScriptApiRegistry} capability modules and
   * generated/typed by `script-api-codegen`. This is the §2/§6 "global services" half of the
   * API; the per-cap fields below (`physics`/`input`/`events`/…) are the transitional
   * hand-written surface that migrates INTO `engine.*` (step 4).
   */
  readonly engine: EngineApi;

  readonly world: WorldCaps;
  readonly time: TimeCaps;
  readonly log: LogCaps;
  /** Convenience access to the bound entity's transform (resolves through `world`). */
  readonly transform: TransformCaps;
  /** Convenience access to the bound entity's sprite (resolves through `world`). */
  readonly sprite: SpriteCaps;
  // NOTE: the global services (physics/input/events/audio/scene) live on `engine.*` only
  // (§2/§6). `InputCaps`/`PhysicsCaps`/`EventCaps` remain as the *backing* shapes a runtime
  // injects via `GameObjectContextDeps` / `buildEngineApi`, not as fields on the context.

  /**
   * Free per-script scoped resources (e.g. the `engine.gpu` GpuScope). ScriptHost fires this
   * after `onDestroy` on script removal / destroyAll / hot reload; implementations must be
   * idempotent. Optional — contexts without scoped resources omit it.
   */
  dispose?(): void;
}

/**
 * The scripting-facing view of {@link IEngineContext} — the W4 `IEngineBridge`, now
 * unified. User Behaviours and script backends receive this. Capability *scoping*
 * (e.g. narrowing `world.spawn`, withholding {@link RawKeyInputCaps}) is enforced by
 * the concrete context implementation, not by the static type.
 */
export type IEngineBridge = IEngineContext;

/**
 * The wider context handed to engine-system modules (e.g. a future C# render2d
 * `ISystem`). Adds raw SoA column-buffer access for batch/zero-copy work. The
 * shape of {@link RawColumnAccess} is filled in once the shared-buffer
 * `ComponentStore` lands (SoA phase); kept minimal here so Phase 0 is additive.
 */
export interface IEngineSystemContext extends IEngineContext {
  readonly columns: RawColumnAccess;
  readonly rawInput: RawKeyInputCaps;
}

/**
 * Raw SoA column access for engine-system modules — placeholder surface, expanded
 * when the single-ArrayBuffer `ComponentStore` is introduced. Engine systems read
 * the same column buffers JS systems write (and, on the native player, a C# `Span<T>`
 * aliases them zero-copy).
 */
export interface RawColumnAccess {
  /** The backing buffer of a component's hot column block, addressed by offset/stride. */
  buffer(token: ComponentToken<unknown>): ArrayBufferLike;
}
